/**
 * How the things BoardFlight draws inside somebody's game look (docs/update-button-plan.md §8).
 *
 * The update card today, and the unlock moment when it is built (docs/achievements-plan.md §7.4.6):
 * the two times BoardFlight is on a player's screen in the middle of a game, so they are one family
 * and take their values from one place. The card follows the dark library the desktop app's window
 * shows (`apps/web/src/storefront/storefront.css` and the design tokens), at the console's type
 * scale, because it is read from across a table and not from a desk.
 *
 * Generated into the layers, never copied: `patcher/dump-constants.mjs` emits this module, and
 * `patcher/generate_config.py` fills the web layer's script and stylesheet from it, the way it
 * fills the feedback button's sizes from `feedback.ts`. A value typed into the layer's CSS instead
 * is the bug this file exists to prevent.
 *
 * - **Colours** are `#RRGGBB` or `#RRGGBBAA`: CSS reads either, and the Android layer turns either
 *   into an `int` (`java_color`). An `rgba()` would be a third spelling for the generator to parse.
 * - **Lengths** are CSS pixels on a Board's 1920-wide panel, which a Board reports at density 1.
 * - **Curves** are cubic-bézier control points, which CSS writes as `cubic-bezier()` and Android
 *   takes as a `PathInterpolator`.
 */
export const LAYER_LOOK = {
    color: {
        /** The library's dialogs and menus. */
        surface: '#17141F',
        /** An inset 1 px line round the surface: ivory at .14. */
        hairline: '#FFFEF124',
        shadow: '#0000008C',
        text: '#FFFEF1',
        /** Ivory at .74, the library's secondary text. */
        textSoft: '#FFFEF1BD',
        /** The library's Play: the one loud button. */
        brand: '#7815FF',
        onBrand: '#FFFEF1',
        /** The library's soft button: ivory at .14, edged at .28. */
        glass: '#FFFEF124',
        glassLine: '#FFFEF147',
        /** The library's primary on dark: Quit, on the card that cannot update. */
        light: '#FFFEF1',
        onLight: '#000000',
        markTile: '#FFFEF1',
        markLift: '#7815FF',
        focus: '#FFFEF1',
        /** The tile under a code. A code stays black on white whatever is round it, so that every
         *  phone still reads it. */
        codeLight: '#FFFFFF',
    },
    card: {
        /** `--radius-lg`, the library's sheets and dialogs. */
        radius: 28,
        width: 640,
        /** With a code to scan beside the words. */
        widthWithCode: 800,
        /** Of the screen, on one narrower than the card. */
        maxWidthPercent: 92,
        top: 24,
        padY: 24,
        padX: 28,
        /** Between the mark, the words and the code; and above the buttons. */
        gap: 20,
        /** Between a title and the line under it. */
        tightGap: 8,
        shadowY: 24,
        shadowBlur: 64,
    },
    /** `BrandMark` (`apps/web/src/kit/primitives.tsx`), with the purple tile lifted as the desktop
     *  app's own page lifts it (`apps/desktop/fallback/fallback.css`). */
    mark: {
        size: 56,
        liftX: 7,
        liftY: -11,
        /** Degrees. */
        liftTurn: 10,
    },
    title: {
        /** `--fs-tv-section`. */
        size: 32,
        weight: 800,
        /** Em. */
        tracking: -0.02,
        lineHeight: 1.15,
        /** A long name is cut at two lines rather than growing the card down the screen. */
        lines: 2,
    },
    body: {
        /** `--fs-tv-label`. */
        size: 24,
        weight: 400,
        lineHeight: 1.35,
    },
    button: {
        height: 52,
        size: 20,
        padX: 26,
        gap: 12,
        /** `--press-scale`: a press squashes, it never changes colour. */
        pressScale: 0.97,
    },
    code: {
        /** The white tile, padding included. */
        size: 168,
        pad: 12,
        radius: 16,
    },
    progress: {
        height: 6,
    },
    focus: {
        width: 2,
        offset: 3,
    },
    motion: {
        /** The library's sheet entrance (`sf-game-rise`), mirrored: it drops in from the top. */
        enterMs: 260,
        exitMs: 160,
        dropPx: 16,
        enterScale: 0.985,
        /** `--ease-out`. */
        ease: [0.2, 0.8, 0.25, 1],
        /** `--dur-fast`, for a press. */
        pressMs: 140,
        /** The purple tile's lift while the card waits on an install: the fallback page's loop. */
        liftMs: 1600,
        /** `--ease-in-out`. */
        liftEase: [0.4, 0, 0.2, 1],
    },
    /**
     * The library's fonts, cut to what the card uses and carried inside the layer
     * (`patcher/web-layer/fonts/`, §8.3), under names no game uses. `fallback` is what draws while
     * they load, where they cannot, and any character outside the cut.
     */
    font: {
        display: 'BoardFlight Display',
        text: 'BoardFlight Text',
        fallback: 'system-ui',
    },
};
/** `fooBar` → `FOO_BAR`. */
function snake(name) {
    return name.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toUpperCase();
}
/**
 * Every token as the text a stylesheet or a script writes, keyed by the placeholder the web layer's
 * templates use for it: `LOOK_COLOR_SURFACE` is `#17141F`, `LOOK_MOTION_EASE` is
 * `cubic-bezier(0.2, 0.8, 0.25, 1)`, `LOOK_CARD_RADIUS` is `28` and the template adds the unit.
 *
 * The one flattening of this module. The generator fills the layer from it, and the playground's
 * copy of the card is held to the layer's stylesheet filled from it, so neither has a second idea of
 * what a token is called or how it is written.
 */
export function layerLookValues() {
    const values = {};
    const walk = (node, path) => {
        for (const [key, value] of Object.entries(node)) {
            const at = [...path, snake(key)];
            if (Array.isArray(value))
                values[at.join('_')] = `cubic-bezier(${value.join(', ')})`;
            else if (value !== null && typeof value === 'object')
                walk(value, at);
            else
                values[at.join('_')] = String(value);
        }
    };
    walk(LAYER_LOOK, ['LOOK']);
    return values;
}
//# sourceMappingURL=layer-look.js.map